import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/data/translations";
import { ArrowRight, Car, Users, Truck, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const icons = {
  "business-car": Car,
  minibus: Users,
  cargo: Truck,
  pallet: Package,
};

const cardColors = ["#FF3B30", "#0A0B10", "#0A0B10", "#FF3B30"];

export default function HomePage() {
  const { lang } = useLang();
  const hero = t.hero[lang];
  const services = t.services[lang];
  const trust = t.trust[lang];

  return (
    <div>
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-[60vh] sm:min-h-[70vh] lg:min-h-[90vh] flex items-center overflow-hidden">
        {/* Background image - right side */}
        <div className="absolute inset-0 hidden lg:block">
          <div className="absolute right-0 top-0 bottom-0 w-1/2">
            <img
              src="https://images.unsplash.com/photo-1652453822974-c7e827141366?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxibGFjayUyME1lcmNlZGVzJTIwQmVueiUyMGNsb3NlJTIwdXAlMjBkZXRhaWwlMjBoZWFkbGlnaHQlMjBncmlsbGV8ZW58MHx8fHwxNzc1Mjk4NjAxfDA&ixlib=rb-4.1.0&q=85"
              alt="Business car"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#FDFDFD] via-[#FDFDFD]/30 to-transparent" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-0 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#FF3B30] mb-4 sm:mb-6" data-testid="hero-overline">
              {hero.overline}
            </p>
            <h1
              data-testid="hero-title"
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-[#0A0B10] mb-4 sm:mb-6"
            >
              {hero.title}
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-[#666666] leading-relaxed mb-8 sm:mb-10 max-w-lg" data-testid="hero-subtitle">
              {hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link to="/contacts" data-testid="hero-cta">
                <Button className="rounded-none bg-[#FF3B30] text-white hover:bg-[#0A0B10] text-sm font-medium px-6 sm:px-8 h-11 sm:h-12 transition-colors w-full sm:w-auto">
                  {hero.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/routes" data-testid="hero-routes-link">
                <Button
                  variant="outline"
                  className="rounded-none border-[#0A0B10] text-[#0A0B10] hover:bg-[#F5F5F5] text-sm font-medium px-6 sm:px-8 h-11 sm:h-12 transition-colors w-full sm:w-auto"
                >
                  {lang === "cs" ? "Trasy a ceny" : "Routes & Prices"}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Mobile hero image */}
        <div className="lg:hidden absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1652453822974-c7e827141366?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxibGFjayUyME1lcmNlZGVzJTIwQmVueiUyMGNsb3NlJTIwdXAlMjBkZXRhaWwlMjBoZWFkbGlnaHQlMjBncmlsbGV8ZW58MHx8fHwxNzc1Mjk4NjAxfDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
        </div>
      </section>

      {/* Trust Band */}
      <section data-testid="trust-band" className="border-y border-[#E5E5E5] bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:justify-between md:gap-0">
            {trust.map((item, i) => (
              <span key={i} className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-[#0A0B10]">
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section data-testid="services-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 md:py-32">
        <div className="mb-10 sm:mb-16">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#FF3B30] mb-3 sm:mb-4">
            {lang === "cs" ? "CO NABIZIME" : "WHAT WE OFFER"}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A0B10]">
            {services.sectionTitle}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-12">
          {services.items.map((service, i) => {
            const Icon = icons[service.id];
            const isRed = cardColors[i] === "#FF3B30";
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Link
                  to={service.link}
                  data-testid={`service-card-${service.id}`}
                  className={`group block p-5 sm:p-8 lg:p-10 border transition-all hover:scale-[1.02] ${
                    isRed
                      ? "bg-[#FF3B30] text-white border-[#FF3B30] hover:bg-[#e0332b]"
                      : "bg-[#0A0B10] text-white border-[#0A0B10] hover:bg-[#1a1b20]"
                  }`}
                >
                  <div className="flex items-start justify-between mb-5 sm:mb-8">
                    <Icon className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
                    <span className="text-xl sm:text-2xl font-heading font-bold">{service.price}</span>
                  </div>
                  <h3 className="font-heading text-lg sm:text-2xl font-medium tracking-tight mb-2 sm:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-xs sm:text-sm leading-relaxed opacity-80 mb-4 sm:mb-6">{service.desc}</p>
                  <div className="flex items-center gap-2 text-xs sm:text-sm font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                    {lang === "cs" ? "Vice info" : "Learn more"}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="bg-[#F5F5F5] border-y border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 md:py-28 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-[#0A0B10] mb-3 sm:mb-4">
            {lang === "cs" ? "Potřebujete přepravu?" : "Need transport?"}
          </h2>
          <p className="text-sm sm:text-base text-[#666666] mb-8 sm:mb-10 max-w-md mx-auto">
            {lang === "cs"
              ? "Zavolejte nám nebo napište — odpovíme do 30 minut."
              : "Call us or write — we reply within 30 minutes."}
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <a href="https://wa.me/420607179165" target="_blank" rel="noreferrer" data-testid="cta-whatsapp">
              <Button className="rounded-none bg-[#FF3B30] text-white hover:bg-[#0A0B10] text-sm font-medium px-8 h-11 sm:h-12 w-full sm:w-auto">
                WhatsApp
              </Button>
            </a>
            <a href="tel:+420607179165" data-testid="cta-phone">
              <Button
                variant="outline"
                className="rounded-none border-[#0A0B10] text-[#0A0B10] hover:bg-[#0A0B10] hover:text-white text-sm font-medium px-8 h-11 sm:h-12 w-full sm:w-auto"
              >
                +420 607 179 165
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
